import {
  Button,
  Col,
  Input,
  message,
  Modal,
  notification,
  Row,
  Tooltip,
  Typography,
  Tag,
} from "antd";
import { EditOutlined, PlusOutlined, TableOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { DatabaseManager } from "helpers/db";
import {
  deleteProcurement,
  getActionableQuote,
  getProcurementDetails,
  updateProcurementStageItem,
} from "apis";
import { ENTITY_TYPES, PROCUREMENT_STATUS_INDICATOR, TOOLTIP_LABELS } from "types";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";
import { IconButton } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import "./ProcurementList.css"

const { Text } = Typography;
const dbManager = new DatabaseManager();

const ProcurementList = ({ filteredProcurementData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [procurementData, setProcurementData] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingProcurement, setDeletingProcurement] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isProcurementDeleted, setIsProcurementDeleted] = useState(false);
  const [sortedData, setSortedData] = useState([]);
  const [selectedProcurement, setSelectedProcurement] = useState(null);
  const [updatedProcurementName, setUpdatedProcurementName] = useState("");
  const [fileURL, setFileURL] = useState("");
  const [loadingRowKey, setLoadingRowKey] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const { procurementName } = location?.state ?? {}
  
  const handleOk = async () => {
    const previousProcurementData = [...procurementData];

    let result = await getProcurementDetails(selectedProcurement.PK);
    if (result.success) {
      let general_data = result?.data?.find(
        (item) => item.SK === selectedProcurement.PK
      );
      let updatedData = {
        ...general_data,
        procurement_name: updatedProcurementName,
      };
      let res = await updateProcurementStageItem(updatedData);
      if (res.success) {
        notification.success({
          message: "Procurement name updated successfully !",
        });
        setIsModalOpen(false);
        setSortedData((prevData) =>
          prevData.map((procurement) =>
            procurement.PK === selectedProcurement.PK
              ? { ...procurement, procurement_name: updatedProcurementName }
              : procurement
          )
        );
      } else {
        notification.error({
          message: "Procurement name update Failed !",
        });
      }
    } else {
      setProcurementData(previousProcurementData);
      notification.error({
        message: "Unable to identify procurement",
      });
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Show delete confirmation modal
  const showDeleteConfirm = (procurement) => {
    setDeletingProcurement(procurement);
    setDeleteModalOpen(true);
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setDeletingProcurement(null);
  };

  // Confirm and execute delete
  const handleDeleteConfirm = async () => {
    if (!deletingProcurement) return;
    
    setIsDeleting(true);
    try {
      await deleteProcurement(deletingProcurement["PK"]);
      notification.success({
        message: "Order deleted successfully",
      });
      setIsProcurementDeleted(true);
    } catch (error) {
      console.error("Procurement deletion failed");
      notification.error({
        message: "Failed to delete order",
      });
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeletingProcurement(null);
    }
  };

  const procurements_data = async () => {
    let res = dbManager.aggregateAllProcurementData(
      process.env.REACT_APP_DYNAMO_DB_PROCUREMENTS || ""
    );

    return res;
  };

  useEffect(() => {
    procurements_data()
      .then((res) => {
        setProcurementData(res?.data);
        ProcurementListSorting(res?.data);
        console.log("new properties added: ", res?.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error fetching procurements",
        });
      });

    location.pathname === "/dashboard" &&
      setProcurementData(filteredProcurementData);
  }, [isProcurementDeleted]);

  const handleEditButton = (selectedProcurement) => {
    setIsModalOpen(true);
    setSelectedProcurement(selectedProcurement);
    setUpdatedProcurementName(selectedProcurement.procurement_name);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "procurement_name",
        header: "Order Name",
        size: 320,
        muiTableBodyCellProps: ({ cell, row }) => ({
          sx: {
            fontWeight: 500,
            color: '#1F2937',
          },
        }),
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Tooltip
              title={renderToolTip(row.original, [["created_at", "created_by"]])}
            >
              <Text
                style={{
                  fontWeight: 500,
                  color: '#1F2937',
                  cursor: 'pointer',
                  flex: 1,
                }}
                ellipsis={{ tooltip: row.original.procurement_name }}
              >
                {row.original.procurement_name}
              </Text>
            </Tooltip>
            <div style={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
              <Tooltip title="Edit name">
                <EditOutlined
                  onClick={() => handleEditButton(row.original)}
                  style={{ 
                    fontSize: 14, 
                    cursor: "pointer", 
                    color: "#00857C",
                    padding: '4px'
                  }}
                />
              </Tooltip>
              <Tooltip title="Delete order">
                <IconButton
                  onClick={() => showDeleteConfirm(row.original)}
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <DeleteOutlineOutlinedIcon
                    sx={{ fontSize: 16, color: "#DC2626" }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "procurement_type",
        header: "Type",
        size: 100,
        Cell: ({ cell }) => (
          <span style={{ 
            fontSize: '12px',
            color: '#6B7280',
            fontWeight: 500,
          }}>
            {cell.getValue() || '—'}
          </span>
        ),
      },
      {
        accessorKey: "sizing_created_at",
        header: "ROBO",
        size: 110,
        Cell: ({ row }) => {
          const status = row.original.sizing_status;
          const date = row.original.sizing_created_at;
          const indicator = PROCUREMENT_STATUS_INDICATOR[status];
          return (
            <Tooltip
              title={renderToolTip(row.original, [
                ["sizing_completed_at", "sizing_completed_by"],
                ["sizing_assessed_at", "sizing_assessed_by"]
              ])}
            >
              <Button
                type="text"
                onClick={() => navigate(`/procurement?PK=${row.original.PK}&stage=Sizing`)}
                style={{ 
                  padding: "4px 8px", 
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {indicator && date && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: indicator.dot,
                    flexShrink: 0,
                  }} />
                )}
                <span style={{ color: '#374151', fontSize: '13px' }}>
                  {date ? moment(date).format("MM/DD/YY") : "—"}
                </span>
              </Button>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "commvault_created_at",
        header: "CommVault",
        size: 110,
        Cell: ({ row }) => {
          const status = row.original.commvault_status;
          const date = row.original.commvault_created_at;
          const indicator = PROCUREMENT_STATUS_INDICATOR[status];
          return (
            <Tooltip
              title={renderToolTip(row.original, [
                ["commvault_completed_at", "commvault_completed_by"],
                ["commvault_assessed_at", "commvault_assessed_by"]
              ])}
            >
              <Button
                type="text"
                onClick={() => navigate(`/procurement?PK=${row.original.PK}&stage=CommVault`)}
                style={{ 
                  padding: "4px 8px", 
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {indicator && date && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: indicator.dot,
                    flexShrink: 0,
                  }} />
                )}
                <span style={{ color: '#374151', fontSize: '13px' }}>
                  {date ? moment(date).format("MM/DD/YY") : "—"}
                </span>
              </Button>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "-",
        header: "Quote",
        size: 120,
        Cell: ({ row }) => (
          <Button
            type="primary"
            size="small"
            loading={loadingRowKey === row.original.PK}
            onClick={() => handleCreateQuote(row.original)}
            disabled={!enableCreateQuoteButton(row.original)}
            style={{
              borderRadius: '6px',
              fontWeight: 500,
              fontSize: '12px',
              height: '28px'
            }}
          >
            {loadingRowKey === row.original.PK ? "..." : "Generate"}
          </Button>
        ),
      },
      {
        accessorKey: "network_created_at",
        header: "Network",
        size: 110,
        Cell: ({ row }) => {
          const status = row.original.network_status;
          const date = row.original.network_created_at;
          const indicator = PROCUREMENT_STATUS_INDICATOR[status];
          return (
            <Tooltip
              title={renderToolTip(row.original, [
                ["network_completed_at", "network_completed_by"],
                ["network_assessed_at", "network_assessed_by"]
              ])}
            >
              <Button
                type="text"
                onClick={() => navigate(`/procurement?PK=${row.original.PK}&stage=Network`)}
                style={{ 
                  padding: "4px 8px", 
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {indicator && date && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: indicator.dot,
                    flexShrink: 0,
                  }} />
                )}
                <span style={{ color: '#374151', fontSize: '13px' }}>
                  {date ? moment(date).format("MM/DD/YY") : "—"}
                </span>
              </Button>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "site_created_at",
        header: "Site",
        size: 110,
        Cell: ({ row }) => {
          const status = row.original.site_status;
          const date = row.original.site_created_at;
          const indicator = PROCUREMENT_STATUS_INDICATOR[status];
          return (
            <Tooltip
              title={renderToolTip(row.original, [
                ["site_completed_at", "site_completed_by"],
                ["site_assessed_at", "site_assessed_by"]
              ])}
            >
              <Button
                type="text"
                onClick={() => navigate(`/procurement?PK=${row.original.PK}&stage=Site`)}
                style={{ 
                  padding: "4px 8px", 
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {indicator && date && (
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: indicator.dot,
                    flexShrink: 0,
                  }} />
                )}
                <span style={{ color: '#374151', fontSize: '13px' }}>
                  {date ? moment(date).format("MM/DD/YY") : "—"}
                </span>
              </Button>
            </Tooltip>
          );
        },
      },
    ],
    [loadingRowKey, navigate]
  );

  const handleCreateQuote = async (procurementData) => {
    setLoadingRowKey(procurementData.PK); 
    // Implement the logic to create a quote using the procurementData
    const {
      estimated_tshirt_size,
      cv,
      speed_gbps_4_port_NICs,
      procurement_type,
      procurement_name,
      enclosure,
      gbps_4_port_NICs,
    } = procurementData;
    try {
      const response = await getActionableQuote(
        estimated_tshirt_size,
        cv,
        enclosure,
        gbps_4_port_NICs,
        speed_gbps_4_port_NICs,
        procurement_name,
        procurement_type
      );
      if (response.success && response.data) {
        if (response.data.url) {
          setFileURL(response.data.url);
          let fileURL = response.data.url;
          if (fileURL) {
            window.open(fileURL);
          }
        } else {
          notification.error({
            message: response.data.message,
            description: "Quote generation failed. Please check inputs.",
            duration: 5,
          });
        }
      }
    } catch (error) {
      message.error(
        error?.message || "Unexpected error while generating quote."
      );
    }finally {
    setLoadingRowKey(null); // stop spinner
  }
  };

  const enableCreateQuoteButton = (procurementData) => {
    if (
      procurementData.sizing_status === "Approved" ||
      procurementData.commvault_status === "Approved" 
      // procurementData.network_status === "Approved"
    ) {
      return true;
    }
    return false;
  };

  const initialState = {
    density: "compact",
    columnPinning: { left: ["mrt-row-actions", "procurement_name"] },
    pagination: { pageSize: 20 },
    showColumnFilters: procurementName ? true : false,
    columnFilters: procurementName ? [
      { id: 'procurement_name', value: procurementName },
    ] : [],
  };

  const ProcurementListSorting = (procurementData) => {
    // Sort the data by created_at in descending order (most recent first)
    const sortedDataProc = procurementData.sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
    setSortedData(sortedDataProc);
  };

  const renderToolTip = (parent, keyPrefixes) => {

    if (!parent || !keyPrefixes || keyPrefixes.length <= 0) return;

    const toolTipBlocks = keyPrefixes
      ?.filter(item => item && item?.length > 0)
      ?.map((itemGroup) => {

        let usernameKey = itemGroup.find(item => item.endsWith("_by"))
        let timestampKey = itemGroup.find(item => item.endsWith("_at"))

        let usernameKeys = usernameKey.split("_")
        let keySuffix = usernameKeys?.length > 2 ? usernameKey.split("_").slice(1).join("_") : usernameKey

        let username = parent[usernameKey] ?? ""
        let timestamp = parent[timestampKey] ?? ""

        if (!username && !timestamp) return null;

        return (
          <div key={keySuffix} style={{ marginBottom: '8px' }}>
            <div style={{ fontWeight: 600, marginBottom: '2px', color: '#E5E7EB' }}>
              {TOOLTIP_LABELS[keySuffix] ?? keySuffix}
            </div>
            <div style={{ fontSize: '12px' }}>
              {username} • {timestamp ? moment(timestamp).format("MM/DD/YY, h:mm A") : ""}
            </div>
          </div>
        )
      })?.filter(item => item);

    if (toolTipBlocks?.length <= 0) return null;

    return <div>{toolTipBlocks}</div>
  }

  const statusLabels = {
    Initial: "In Progress",
    Submitted: "Ready for Review",
    Approved: "Completed",
  };

  return (
    <div className="admin-dashboard">
      {/* Page Header */}
      <div className="admin-dashboard-header">
        <div className="admin-dashboard-header-left">
          <h1>
            <TableOutlined style={{ color: '#00857C' }} />
            Admin Dashboard
          </h1>
          <p>Track and manage all procurement orders across stages</p>
        </div>
        <div className="admin-dashboard-header-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate("/cluster/initialize")}
            style={{
              height: '44px',
              padding: '0 24px',
              fontWeight: 600,
              borderRadius: '8px',
            }}
          >
            New Order
          </Button>
        </div>
      </div>

      {/* Table Container */}
      <div className="admin-dashboard-table-container">
        <div className="admin-dashboard-table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={sortedData}
            enableColumnPinning={true}
            initialState={initialState}
            enableStickyHeader={true}
            enableColumnResizing={true}
            columnResizeMode="onChange"
            layoutMode="semantic"
            renderTopToolbarCustomActions={() => (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}>
                <span style={{ 
                  fontSize: "12px", 
                  color: "#6B7280",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em"
                }}>
                  Status:
                </span>
                {Object.entries(PROCUREMENT_STATUS_INDICATOR).map(([status, colors]) => (
                  <div key={status} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span 
                      style={{ 
                        width: "8px", 
                        height: "8px", 
                        borderRadius: "50%", 
                        backgroundColor: colors.dot,
                        display: "inline-block"
                      }}
                    />
                    <span style={{ 
                      fontSize: "12px", 
                      color: "#374151",
                      fontWeight: 500
                    }}>
                      {statusLabels[status] || status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            muiTableProps={{
              sx: {
                borderCollapse: "separate",
                borderSpacing: "0",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F9FAFB",
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#6B7280',
                borderBottom: '1px solid #E5E7EB',
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                borderBottom: '1px solid #F3F4F6',
                fontSize: '13px',
                color: '#374151',
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "none",
                height: "auto",
                overflowY: "visible",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
            }}
          />
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        title="Edit Order Name"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save Changes"
        cancelText="Cancel"
        centered
        okButtonProps={{
          style: { fontWeight: 500 }
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <Text style={{ fontWeight: 500, color: '#374151' }}>
            Order Name
          </Text>
        </div>
        <Input
          value={updatedProcurementName}
          onChange={(e) => setUpdatedProcurementName(e.target.value)}
          placeholder="Enter order name"
          style={{ borderRadius: '6px' }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Order"
        open={deleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        centered
        confirmLoading={isDeleting}
        okButtonProps={{
          danger: true,
          style: { fontWeight: 500 }
        }}
        cancelButtonProps={{
          style: { fontWeight: 500 }
        }}
      >
        <div style={{ padding: '8px 0' }}>
          <Text style={{ color: '#374151', fontSize: '14px' }}>
            Are you sure you want to delete this order?
          </Text>
          {deletingProcurement && (
            <div style={{ 
              marginTop: '12px', 
              padding: '12px', 
              background: '#F9FAFB', 
              borderRadius: '6px',
              border: '1px solid #E5E7EB'
            }}>
              <Text strong style={{ color: '#111827' }}>
                {deletingProcurement.procurement_name}
              </Text>
            </div>
          )}
          <Text type="secondary" style={{ display: 'block', marginTop: '12px', fontSize: '13px' }}>
            This action cannot be undone.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ProcurementList;
